import React, { useEffect, useRef, useState } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { gsap } from 'gsap'

import { PremiumBadge } from 'components/atoms/premiumBadge'
import { assetImagePath } from 'interfaces/constants'

import { StyledBannerContainer, StyledContentArea } from './index.styled'

export type BannerProps = {
  title: string
  description: string
  banner: string
  desktopBanner: string
  position: string
}

type Props = {
  isMobile: boolean
  banners: BannerProps[]
  topbarHeight?: number
  isPremium?: boolean
  applyH1?: boolean
}
const BlockingAnimationTime = 1000
const DEFAULT_BANNER_HEIGHT_DESKTOP = 603
const DEFAULT_BANNER_HEIGHT_MOBILE = 831

const AnimationBanner = ({
  isMobile,
  banners,
  topbarHeight = 64,
  isPremium = false,
  applyH1,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const enableSlide = useRef(true)
  const enableWheel = useRef(true)
  const bannerContent = useRef<HTMLDivElement>(null)

  const defaultBannerHeight = isMobile
    ? DEFAULT_BANNER_HEIGHT_MOBILE
    : DEFAULT_BANNER_HEIGHT_DESKTOP
  const [bannerHeight, setBannerHeight] = useState(defaultBannerHeight)
  const tl = useRef({})
  const indx = useRef({
    index: 0,
  })
  const el = useRef<HTMLDivElement>(null)
  const q = gsap.utils.selector(el)
  const swipeYBegin = useRef({
    begin: -1,
  })

  const slideDown = () => {
    if (enableSlide.current) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)
      tl.current = gsap
        .timeline()
        .fromTo(
          q('.animation-banner-post'),
          { opacity: 1, y: 0 },
          {
            opacity: 0,
            y: '-100%',
            duration: 0.5,
          },
        )
        .fromTo(
          q('.animation-banner-main'),
          { opacity: 0, y: '100%' },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
          },
          0,
        )
        .from(q('.animation-content'), { opacity: 0, duration: 1, y: 100 })
        .fromTo(
          q('.down-icon'),
          { opacity: 0, duration: 1, y: -50 },
          { opacity: 1, duration: 1, y: 0 },
        )
    }
  }

  const slideUp = () => {
    if (enableSlide.current) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)
      tl.current = gsap
        .timeline()
        .fromTo(
          q('.animation-banner-main'),
          { opacity: 0, y: '-100%' },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
          },
          0,
        )
        .to(q('.animation-banner-after'), { opacity: 0, y: '100%' }, 0)
        .from(q('.animation-content'), { opacity: 0, duration: 1, y: 100 })
        .fromTo(
          q('.down-icon'),
          { opacity: 0, duration: 1, y: -50 },
          { opacity: 1, duration: 1, y: 0 },
        )
    }
  }

  useEffect(() => {
    if (indx.current.index <= currentIndex) {
      slideDown()
    } else {
      slideUp()
    }
    indx.current.index = currentIndex
  }, [currentIndex])

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.innerHeight > 0) {
      setBannerHeight(window?.innerHeight - topbarHeight)
    }
  }, [])

  useEffect(() => {
    const advancedSetBannerHeight = () => {
      if (!bannerContent.current) {
        return
      }

      const minBannerHeight =
        bannerContent.current.scrollHeight +
        bannerContent.current.offsetTop +
        40

      setBannerHeight(Math.max(minBannerHeight, defaultBannerHeight))
    }
    advancedSetBannerHeight()

    window.addEventListener('resize', advancedSetBannerHeight, {
      passive: true,
    })

    return () => {
      window.removeEventListener('resize', advancedSetBannerHeight)
    }
  }, [defaultBannerHeight])

  // const handleSlideUp = () => {
  //   if (enableWheel.current && enableSlide.current) {
  //     enableWheel.current = false
  //     setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
  //     setTimeout(() => {
  //       enableWheel.current = true
  //     }, BlockingAnimationTime)
  //   }
  // }

  const handleWheel = (e: React.WheelEvent) => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)

      if (e.deltaY > 0) {
        typeof window !== 'undefined' &&
          currentIndex < banners.length - 1 &&
          window.scrollTo({
            top: (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        document.body.classList.toggle(
          'noscroll',
          currentIndex < banners.length - 1,
        )
        setCurrentIndex((v) =>
          v > banners.length - 2 ? banners.length - 1 : v + 1,
        )
      } else {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        document.body.classList.toggle('noscroll', currentIndex > 0)
        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      }
    }
  }

  const handleSlide = (type: 'up' | 'down') => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      if (type === 'up') {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      } else {
        typeof window !== 'undefined' &&
          currentIndex < banners.length - 1 &&
          window.scrollTo({
            top: (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })
        setCurrentIndex((v) =>
          v > banners.length - 2 ? banners.length - 1 : v + 1,
        )
      }

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)
    }
  }

  return (
    <StyledBannerContainer
      ref={el}
      onTouchStart={(e) => {
        swipeYBegin.current.begin = e.changedTouches[0].clientY
        typeof window !== 'undefined' &&
          document.body.classList.toggle(
            'noscroll',
            currentIndex < banners.length - 1,
          )
      }}
      onTouchEnd={(e) => {
        if (swipeYBegin.current.begin > 0) {
          handleSlide(
            e.changedTouches[0].clientY - swipeYBegin.current.begin > 50
              ? 'up'
              : 'down',
          )
          swipeYBegin.current.begin = -1
        }

        typeof window !== 'undefined' &&
          document.body.classList.toggle('noscroll', false)
      }}
      onWheel={handleWheel}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100vh',
          maxHeight: '100%',
          width: '100%',
        }}
      >
        {isPremium && <PremiumBadge className="premium-badge" />}

        <StyledContentArea
          // className="animation animation-content"
          data-position={
            banners[currentIndex] &&
            (banners[currentIndex].title || banners[currentIndex].description)
              ? banners[currentIndex].position || 'bottom'
              : 'bottom'
          }
          // ref={bannerContent}
        >
          {banners[currentIndex] &&
            (banners[currentIndex].title ||
              banners[currentIndex].description) && (
              <div className="content">
                <Heading
                  className="title"
                  tag="h1"
                  as={applyH1 && currentIndex === 0 ? 'h1' : 'h2'}
                  color="white"
                >
                  {banners[currentIndex]?.title}
                </Heading>
                <span
                  className="description"
                  dangerouslySetInnerHTML={{
                    __html: banners[currentIndex]?.description,
                  }}
                ></span>
              </div>
            )}
          <img
            className="down-icon"
            src={`${assetImagePath}/downArrow.png`}
            alt="down-icon"
            loading="lazy"
            onClick={() => {
              if (currentIndex < banners.length - 1) {
                if (enableSlide) {
                  setCurrentIndex((v) =>
                    v > banners.length - 2 ? banners.length - 1 : v + 1,
                  )
                }
              } else {
                if (typeof window !== 'undefined') {
                  document.body.classList.toggle('noscroll', false)
                  window.scrollTo({
                    top: el.current ? el.current.clientHeight : bannerHeight,
                    behavior: 'smooth',
                  })
                }
              }
            }}
          />
        </StyledContentArea>
      </div>
      {banners.map((banner, index) => (
        <img
          key={index}
          loading="lazy"
          className={`animation animation-banner${
            index < currentIndex ? ' animation-banner-post' : ''
          }${currentIndex === index ? ' animation-banner-main' : ''}${
            currentIndex < index ? ' animation-banner-after' : ''
          }`}
          src={isMobile ? banner?.banner : banner?.desktopBanner}
          alt={
            banners[currentIndex]?.title || banners[currentIndex]?.description
          }
        />
      ))}
    </StyledBannerContainer>
  )
}

export { AnimationBanner }
